import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useRestrictToLoggedInAndVerified } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedInAndVerified';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Plus from '@wix/da-ds/pkg/Icons/Plus';
import Check from '@wix/da-ds/pkg/Icons/Check';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { useThrottle } from '@wix/da-shared-react/pkg/utils/hooks/useThrottle';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

export interface Props {
  group: PapiUser;
  className?: string;
  requestJoin: (group: PapiUser) => void;
}

const JoinGroupButton: React.FC<Props> = ({
  group,
  requestJoin,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const ncNew = useFlag('nc_new');

  // throttle those button presses so there's time for any notifications to disappear/backend requests to complete
  const requestJoinThrottled = useThrottle(
    () => requestJoin(group),
    [requestJoin, group],
    6000
  );

  const { isLoggedIn, isVerificationNeeded, restrictToLoggedInAndVerified } =
    useRestrictToLoggedInAndVerified();

  const loggedInAndVerified = isLoggedIn && !isVerificationNeeded;
  const biData = loggedInAndVerified
    ? {
        evid: BiEventType.JOIN_GROUP,
        groupid: group.useridUuid,
        sectionname: 'button',
      }
    : undefined;
  const onClick = restrictToLoggedInAndVerified(
    ReferralType.JOIN_GROUP,
    'joingroup',
    requestJoinThrottled
  );

  if (group.extendedGroup?.isAdmin) {
    return (
      <GhostButton
        size="small"
        variant="approval-green"
        className={className}
        href={Url.groupNotificationsLink(ncNew, group.userId)}
        biData={{
          evid: BiEventType.VIEW_GROUP_NOTIFICATION,
          typeid: ResourceType.GROUP,
          itemid: group.userId,
          groupid: group.useridUuid,
          sectionname: 'button',
        }}
      >
        {t('subnavigation.button.notifications')}
      </GhostButton>
    );
  }

  if (group.extendedGroup?.isIngroup) {
    return (
      <TextButton
        size="small"
        variant="main"
        className={className}
        icon={Check}
        disabled
        noHorizontalPaddings={false}
      >
        {t('subnavigation.button.memberOfGroup')}
      </TextButton>
    );
  }

  if (isMobile) {
    return (
      <GhostButton
        variant="approval-green"
        size="small"
        className={className}
        onClick={onClick}
        biData={biData}
      >
        {t('subnavigation.button.joinGroup')}
      </GhostButton>
    );
  }

  return (
    <BrandedButton
      variant="brand-green"
      size="small"
      icon={Plus}
      className={className}
      onClick={onClick}
      biData={biData}
    >
      {t('subnavigation.button.joinGroup')}
    </BrandedButton>
  );
};
JoinGroupButton.displayName = 'JoinGroupButton';

export default JoinGroupButton;
